import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';

import { addPageData, navigateWizard } from '../../actions';
import Button from '../../components/Button';
import PageLayout from '../../components/PageLayout';
import analytics from '../../lib/analytics';
import * as payloadFactory from '../../lib/pageDataPayloadFactory';
import { otherFundsPropTypes, periodPropTypes } from '../../reducers/propTypes';
import {
  periodsPerClassIdSelector,
  periodSelector,
  otherFundsSelector,
} from '../../reducers/selectors';
import OtherLoanFundsModal from '../modals/OtherLoanFundsModal';
import ScholarshipsFundsModal from '../modals/ScholarshipsFundsModal';
import SponsorshipFundsModal from '../modals/SponsorshipFundsModal';

export const hasNoFundsPayload = () =>
  payloadFactory.createAddPageDataPayload(
    'otherFunds',
    {
      scholarship: {
        previous: { currency: null, amount: null },
        period: { currency: null, amount: null },
        remainder: { currency: null, amount: null },
      },
      sponsorship: {
        previous: { currency: null, amount: null },
        period: { currency: null, amount: null },
        remainder: { currency: null, amount: null },
      },
      loans: {
        previous: { currency: null, amount: null },
        period: { currency: null, amount: null },
        remainder: { currency: null, amount: null },
      },
    },
    'otherFunds',
    {
      scholarshipPreviousCurrency: null,
      scholarshipPreviousAmount: null,
      scholarshipPeriodCurrency: null,
      scholarshipPeriodAmount: null,
      scholarshipRemainderCurrency: null,
      scholarshipRemainderAmount: null,
      sponsorshipPreviousCurrency: null,
      sponsorshipPreviousAmount: null,
      sponsorshipPeriodCurrency: null,
      sponsorshipPeriodAmount: null,
      sponsorshipRemainderCurrency: null,
      sponsorshipRemainderAmount: null,
      loansPreviousCurrency: null,
      loansPreviousAmount: null,
      loansPeriodCurrency: null,
      loansPeriodAmount: null,
      loansRemainderCurrency: null,
      loansRemainderAmount: null,
    },
  );

const OtherFundsPage = ({
  dispatchAddPageData,
  dispatchWizardNav,
  otherFunds,
  periods,
  period,
}) => {
  const { scholarship, sponsorship, loans } = otherFunds;

  const totalFunds = [
    scholarship.previous.amount,
    scholarship.period.amount,
    scholarship.remainder.amount,
    sponsorship.previous.amount,
    sponsorship.period.amount,
    sponsorship.remainder.amount,
    loans.previous.amount,
    loans.period.amount,
    loans.remainder.amount,
  ].reduce((total, amount) => {
    const parsedAmount = Number.parseFloat(amount) || 0;
    return total + parsedAmount;
  }, 0);

  const hasNoFunds = isNaN(totalFunds) || totalFunds === 0;

  const [hasChanged, setHasChanged] = useState(false);

  const handleApplyDefault = event => {
    event.preventDefault();

    analytics.sendEvent("Click - I don't have any funds from these sources yet.", 'other-funds');

    dispatchAddPageData(hasNoFundsPayload());
    dispatchWizardNav({ dir: 'fwd' });
  };

  const handleSubmit = event => {
    event.preventDefault();

    analytics.sendEvent('Click - Next', 'other-funds');

    dispatchWizardNav({ dir: 'fwd' });
  };

  if (hasChanged) {
    const clearCapOnLoanPayload = payloadFactory.createResetPageObjectPayLoad('capOnLoan', {
      maxLoanAmount: null,
      futureMaxLoanAmount: null,
    });
    dispatchAddPageData(clearCapOnLoanPayload);
  }

  const serverValueFrom = uiValue => ({
    scholarshipPreviousCurrency: uiValue.scholarship.previous.currency,
    scholarshipPreviousAmount: uiValue.scholarship.previous.amount,
    scholarshipPeriodCurrency: uiValue.scholarship.period.currency,
    scholarshipPeriodAmount: uiValue.scholarship.period.amount,
    scholarshipRemainderCurrency: uiValue.scholarship.remainder.currency,
    scholarshipRemainderAmount: uiValue.scholarship.remainder.amount,
    sponsorshipPreviousCurrency: uiValue.sponsorship.previous.currency,
    sponsorshipPreviousAmount: uiValue.sponsorship.previous.amount,
    sponsorshipPeriodCurrency: uiValue.sponsorship.period.currency,
    sponsorshipPeriodAmount: uiValue.sponsorship.period.amount,
    sponsorshipRemainderCurrency: uiValue.sponsorship.remainder.currency,
    sponsorshipRemainderAmount: uiValue.sponsorship.remainder.amount,
    loansPreviousCurrency: uiValue.loans.previous.currency,
    loansPreviousAmount: uiValue.loans.previous.amount,
    loansPeriodCurrency: uiValue.loans.period.currency,
    loansPeriodAmount: uiValue.loans.period.amount,
    loansRemainderCurrency: uiValue.loans.remainder.currency,
    loansRemainderAmount: uiValue.loans.remainder.amount,
  });

  const onUpdateScholarship = newScholarship => {
    if (haveValuesChanged(newScholarship, otherFunds.scholarship)) {
      setHasChanged(true);
    }
    storePageData({
      ...otherFunds,
      scholarship: newScholarship,
    });
  };

  const onUpdateSponsorship = newSponsorship => {
    if (haveValuesChanged(newSponsorship, otherFunds.sponsorship)) {
      setHasChanged(true);
    }
    storePageData({
      ...otherFunds,
      sponsorship: newSponsorship,
    });
  };

  const onUpdateLoans = newLoans => {
    if (haveValuesChanged(newLoans, otherFunds.loans)) {
      setHasChanged(true);
    }
    storePageData({
      ...otherFunds,
      loans: newLoans,
    });
  };

  const storePageData = uiValue => {
    const payload = payloadFactory.createAddPageDataPayload(
      'otherFunds',
      uiValue,
      'otherFunds',
      serverValueFrom(uiValue),
    );

    dispatchAddPageData(payload);
  };

  const haveValuesChanged = (fund, oldValue) => {
    return (
      oldValue.period.amount !== fund.period.amount ||
      oldValue.remainder.amount !== fund.remainder.amount ||
      oldValue.previous.amount !== fund.previous.amount
    );
  };

  const renderNoFundsLink = () => {
    if (hasNoFunds) {
      return (
        <div className="row-spacer">
          <Link to="/" onClick={handleApplyDefault}>
            I don&apos;t have any funds from these sources yet.
          </Link>
        </div>
      );
    }

    return null;
  };

  return (
    <PageLayout
      title="Do you have funds from any of these sources?"
      helpText="We need to know this upfront because some schools require us to deduct these amounts
                from the maximum we can lend you. We'll confirm this again when we finalise your loan."
    >
      <form onSubmit={handleSubmit}>
        <div className="row text-center text-left--small">
          <div className="col col--auto">
            <ScholarshipsFundsModal
              scholarship={scholarship}
              onUpdateScholarship={onUpdateScholarship}
              periods={periods}
              currentPeriod={period}
            />
          </div>

          <div className="col col--auto">
            <SponsorshipFundsModal
              sponsorship={sponsorship}
              onUpdateSponsorship={onUpdateSponsorship}
              periods={periods}
              currentPeriod={period}
            />
          </div>

          <div className="col col--auto">
            <OtherLoanFundsModal
              loans={loans}
              onUpdateLoans={onUpdateLoans}
              periods={periods}
              currentPeriod={period}
            />
          </div>
        </div>

        {renderNoFundsLink()}

        <div className="row-spacer">
          <Button type="submit" isDisabled={hasNoFunds}>
            Next
          </Button>
        </div>
      </form>
    </PageLayout>
  );
};

OtherFundsPage.propTypes = {
  dispatchAddPageData: PropTypes.func.isRequired,
  dispatchWizardNav: PropTypes.func.isRequired,
  periods: PropTypes.arrayOf(periodPropTypes).isRequired,
  otherFunds: otherFundsPropTypes.isRequired,
  period: periodPropTypes.isRequired,
};

// This is a hack and should be deleted if the app can handle returning users with out of date 'in progress' applications.
const addPreviousFieldsHack = reduxStore => {
  const otherFunds = otherFundsSelector(reduxStore);
  const { scholarship, sponsorship, loans } = otherFunds;

  if (!scholarship.previous) {
    otherFunds.scholarship.previous = { currency: '', amount: '' };
  }

  if (!sponsorship.previous) {
    otherFunds.sponsorship.previous = { currency: '', amount: '' };
  }

  if (!loans.previous) {
    otherFunds.loans.previous = { currency: '', amount: '' };
  }

  return otherFunds;
};

export const mapStateToProps = reduxStore => ({
  otherFunds: addPreviousFieldsHack(reduxStore),
  periods: periodsPerClassIdSelector(reduxStore),
  period: periodSelector(reduxStore),
});

export const mapDispatchToProps = {
  dispatchAddPageData: addPageData,
  dispatchWizardNav: navigateWizard,
};

export { OtherFundsPage };

export default connect(mapStateToProps, mapDispatchToProps)(OtherFundsPage);
